/**
 * Utilitário para normalizar dados vindos do backend para uso em formulários React
 * Resolve o problema de "controlled vs uncontrolled components"
 * 
 * @description
 * O backend pode retornar valores null/undefined, mas os formulários React precisam
 * de valores consistentes para evitar warnings de controlled/uncontrolled components.
 */

import { Collaborator } from '@/models';
import { normalizeVacationPeriods } from '@/features/Collaborators/Models/vacationPeriodSell';

/**
 * Normaliza strings null/undefined para string vazia
 */
const normalizeString = (value: string | null | undefined): string => value ?? '';

/**
 * Normaliza booleans null/undefined para false
 */
const normalizeBoolean = (value: boolean | null | undefined): boolean => value ?? false;

/**
 * Normaliza arrays null/undefined para array vazio
 */
const normalizeArray = <T>(value: T[] | null | undefined): T[] => value ?? [];

const createEmptyAddress = () => ({
    fullAddress: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    latitude: 0,
    longitude: 0,
    proofOfAddressUrl: '',
});

/**
 * Normaliza arrays de endereços garantindo que todos os campos string sejam vazios ao invés de null/undefined
 */
const normalizeAddresses = (addresses: any[] | null | undefined) => {
    const normalized: any[] = [];

    // Ensure we have at least 2 indices (0 = main, 1 = mailing)
    const source = Array.isArray(addresses) ? addresses : [];
    for (let i = 0; i < 2; i++) {
        const addr = source[i] || {};
        normalized[i] = {
            ...createEmptyAddress(),
            ...addr,
            fullAddress: normalizeString(addr.fullAddress),
            street: normalizeString(addr.street),
            number: normalizeString(addr.number),
            complement: normalizeString(addr.complement),
            neighborhood: normalizeString(addr.neighborhood),
            city: normalizeString(addr.city),
            state: normalizeString(addr.state),
            country: normalizeString(addr.country),
            zipCode: normalizeString(addr.zipCode),
            proofOfAddressUrl: normalizeString(addr.proofOfAddressUrl),
            latitude: addr.latitude ?? 0,
            longitude: addr.longitude ?? 0,
            type: addr.type ?? (i === 0 ? 'main' : 'mailing'),
        };
    }

    return normalized;
};

/**
 * Normaliza objeto de documento de identidade garantindo que todos os campos string sejam vazios ao invés de null/undefined
 */
const normalizeIdentityDocument = (identityDocument: any) => {
    if (!identityDocument || typeof identityDocument !== 'object') {
        return {
            number: '',
            issuer: '',
            uf: '',
            issueDate: '',
            idDocumentFrontUrl: '',
            idDocumentBackUrl: '',
        };
    }

    return {
        ...identityDocument,
        number: normalizeString(identityDocument.number),
        issuer: normalizeString(identityDocument.issuer),
        uf: normalizeString(identityDocument.uf),
        issueDate: normalizeString(identityDocument.issueDate),
        idDocumentFrontUrl: normalizeString(identityDocument.idDocumentFrontUrl),
        idDocumentBackUrl: normalizeString(identityDocument.idDocumentBackUrl),
    };
};

/**
 * Normaliza objeto de contato de emergência garantindo que todos os campos string sejam vazios ao invés de null/undefined
 */
const normalizeEmergencyContact = (emergencyContact: any) => {
    if (!emergencyContact || typeof emergencyContact !== 'object') {
        return {
            name: '',
            phone: '',
            relationship: '',
        };
    }

    return {
        ...emergencyContact,
        name: normalizeString(emergencyContact.name),
        phone: normalizeString(emergencyContact.phone),
        relationship: normalizeString(emergencyContact.relationship),
    };
};

/**
 * Normaliza objeto de conta bancária garantindo que todos os campos string sejam vazios ao invés de null/undefined
 */
const normalizeBankAccount = (bankAccount: any) => {
    if (!bankAccount || typeof bankAccount !== 'object') {
        return {
            bankCode: '',
            agency: '',
            account: '',
            digit: '',
            pixKey: '',
        };
    }

    return {
        ...bankAccount,
        bankCode: normalizeString(bankAccount.bankCode),
        agency: normalizeString(bankAccount.agency),
        account: normalizeString(bankAccount.account),
        digit: normalizeString(bankAccount.digit),
        pixKey: normalizeString(bankAccount.pixKey),
    };
};

/**
 * Normaliza dados de colaborador vindos do backend para uso em formulários
 * 
 * @param backendData - Dados vindos do backend que podem ter null/undefined
 * @returns Dados normalizados para formulário (strings vazias, booleans false, arrays vazios)
 * 
 * @example
 * ```typescript
 * // ❌ Antes - dados do backend causavam warning
 * const backendData = { personalEmail: null, addresses: null };
 * 
 * // ✅ Depois - dados normalizados para formulário
 * const formData = normalizeCollaboratorFormData(backendData);
 * // Result: { personalEmail: '', addresses: [], ... }
 * ```
 */
export const normalizeCollaboratorFormData = (backendData: Collaborator | null | undefined): Partial<Collaborator> => {
    if (!backendData) return {};

    return {
        ...backendData,

        // 📧 Campos de contato
        personalEmail: normalizeString(backendData.personalEmail),
        phone: normalizeString(backendData.phone),

        // 👤 Dados pessoais
        birthDate: normalizeString(backendData.birthDate),
        nationality: normalizeString(backendData.nationality),
        naturality: normalizeString(backendData.naturality),
        birthState: normalizeString(backendData.birthState),

        // 📄 Documentos
        cpf: normalizeString(backendData.cpf),
        esocialCode: normalizeString(backendData.esocialCode),

        // 🏠 Endereços
        addresses: normalizeAddresses(backendData.addresses),

        // 👨‍👩‍👧‍👦 Família
        motherName: normalizeString(backendData.motherName),
        fatherName: normalizeString(backendData.fatherName),
        spouseName: normalizeString(backendData.spouseName),

        // 💼 Trabalho
        departmentId: normalizeString(backendData.departmentId),
        responsibleId: normalizeString(backendData.responsibleId),
        clientResponsible: normalizeString(backendData.clientResponsible),
        cnpj: normalizeString(backendData.cnpj),
        companyName: normalizeString(backendData.companyName),

        // 🎓 Estágio
        internshipEducationalInstitution: normalizeString(backendData.internshipEducationalInstitution),
        internshipCourse: normalizeString(backendData.internshipCourse),
        internshipContractUrl: normalizeString(backendData.internshipContractUrl),

        // 🏥 Saúde
        healthInsuranceProvider: normalizeEnum(backendData.healthInsuranceProvider),
        healthInsuranceInclusionDate: normalizeString(backendData.healthInsuranceInclusionDate),
        healthInsuranceCardNumber: normalizeString(backendData.healthInsuranceCardNumber),

        // 🎁 Brindes e preferências
        slipperSize: normalizeString(backendData.slipperSize),
        freeTimeActivities: normalizeString(backendData.freeTimeActivities),
        favoriteFoodAndDrink: normalizeString(backendData.favoriteFoodAndDrink),
        favoriteColor: normalizeString(backendData.favoriteColor),
        favoriteBook: normalizeString(backendData.favoriteBook),
        favoriteMovieOrSeries: normalizeString(backendData.favoriteMovieOrSeries),
        favoriteCharacter: normalizeString(backendData.favoriteCharacter),
        sportsPractice: normalizeString(backendData.sportsPractice),
        specialTalent: normalizeString(backendData.specialTalent),
        futureGoals: normalizeString(backendData.futureGoals),
        generalObservations: normalizeString(backendData.generalObservations),

        // 📋 URLs de contratos e documentos
        workContractUrl: normalizeString(backendData.workContractUrl),
        ndaUrl: normalizeString(backendData.ndaUrl),
        equipmentUsageUrl: normalizeString(backendData.equipmentUsageUrl),
        imageRightsUrl: normalizeString(backendData.imageRightsUrl),
        admissionExamUrl: normalizeString(backendData.admissionExamUrl),
        terminationExamUrl: normalizeString(backendData.terminationExamUrl),

        // 💰 Salário
        newSalaryEntryDate: normalizeString(backendData.newSalaryEntryDate),
        newSalaryEntrySalaryType: normalizeString(backendData.newSalaryEntrySalaryType),
        newSalaryEntryReason: normalizeString(backendData.newSalaryEntryReason),
        newSalaryEntryObservations: normalizeString(backendData.newSalaryEntryObservations),

        // ✅ Booleans que devem ser false por padrão
        isMailingSameAsMain: normalizeBoolean(backendData.isMailingSameAsMain),
        hasHealthInsurance: normalizeBoolean(backendData.hasHealthInsurance),
        isPCD: normalizeBoolean(backendData.isPCD),
        hasCajuBenefit: normalizeBoolean(backendData.hasCajuBenefit),
        hasWellhubBenefit: normalizeBoolean(backendData.hasWellhubBenefit),
        hasZeloBenefit: normalizeBoolean(backendData.hasZeloBenefit),
        hasGuapecoBenefit: normalizeBoolean(backendData.hasGuapecoBenefit),
        showVacationPeriods: normalizeBoolean(backendData.showVacationPeriods),
        isRaroAcademyAlumnus: normalizeBoolean(backendData.isRaroAcademyAlumnus),
        hasOnboardingKit: normalizeBoolean(backendData.hasOnboardingKit),
        hasBackpack: normalizeBoolean(backendData.hasBackpack),
        // IMPORTANTE: Não definir esses campos se forem undefined
        // para não sobrescrever valores existentes no banco
        salaryHistoryShowHistory: backendData.salaryHistoryShowHistory ?? undefined,
        salaryHistoryAllowRemove: backendData.salaryHistoryAllowRemove ?? undefined,

        // 📋 Arrays que devem ser vazios por padrão
        children: normalizeArray(backendData.children),
        healthDependents: normalizeArray(backendData.healthDependents),
        medicalCertificates: normalizeArray(backendData.medicalCertificates),
        zeloDependents: normalizeArray(backendData.zeloDependents),
        pcdTypes: normalizeArray(backendData.pcdTypes),
        vacationPeriods: normalizeVacationPeriods(backendData.vacationPeriods),
        occurrences: normalizeArray(backendData.occurrences),
        raroFeedbacks: normalizeArray(backendData.raroFeedbacks),
        clientFeedbacks: normalizeArray(backendData.clientFeedbacks),
        salaryHistorySalaries: normalizeArray(backendData.salaryHistorySalaries),
        resources: normalizeArray(backendData.resources),

        // 📄 Documentos de identidade
        identityDocument: normalizeIdentityDocument(backendData.identityDocument),

        // 📞 Contatos de emergência
        emergencyContact: normalizeEmergencyContact(backendData.emergencyContact),

        // 💳 Contas bancárias
        salaryAccount: normalizeBankAccount(backendData.salaryAccount),
        personalAccount: normalizeBankAccount(backendData.personalAccount),
    };
};

/**
 * Helper genérico para normalizar qualquer objeto de formulário
 * Útil para outros tipos de formulário além de colaborador
 */
export const normalizeFormData = <T extends Record<string, any>>(data: T | null | undefined): Partial<T> => {
    if (!data) return {};

    const normalized: any = { ...data };

    // Normalizar recursivamente todos os campos
    Object.keys(normalized).forEach(key => {
        const value = normalized[key];

        if (value === null || value === undefined) {
            // Determinar tipo padrão baseado no nome do campo
            if (typeof value === 'string' || key.includes('Name') || key.includes('Email') || key.includes('Url')) {
                normalized[key] = '';
            } else if (typeof value === 'boolean' || key.startsWith('is') || key.startsWith('has') || key.startsWith('show')) {
                normalized[key] = false;
            } else if (Array.isArray(value)) {
                normalized[key] = [];
            }
        }
    });

    return normalized;
};

const normalizeEnum = <T extends string>(value: T | null | undefined): T | undefined => {
    return value ?? undefined;
}; 